import classNames from "classnames";

import {
  LoaderCircle as LoaderCircleIcon,
  type LucideProps,
} from "@/shared/foundation/icons";

import styles from "./Spinner.module.css";

export interface SpinnerProps extends Omit<LucideProps, "size" | "children"> {
  /**
   * Diameter. `sm` matches body text, `md` sits alongside a button label, `lg`
   * is for a standalone loading region. Defaults to `sm`.
   */
  size?: "sm" | "md" | "lg";
}

/**
 * Indeterminate loading indicator. Drawn in `currentColor`, so it takes the
 * color of whatever text it sits next to rather than needing one passed in.
 *
 * Announces itself as a polite live region named "Loading". Native attributes
 * spread last, so a caller overrides that with their own `aria-label`, or drops
 * out of the accessibility tree with `aria-hidden` — which is what a consumer
 * that already owns a `role="status"` region should do rather than nesting two
 * live regions.
 */
export function Spinner(props: SpinnerProps) {
  const { size = "sm", className, ...rest } = props;

  return (
    <LoaderCircleIcon
      role="status"
      aria-label="Loading"
      className={classNames(styles.spinner, styles[size], className)}
      {...rest}
    />
  );
}
